// region Store
import {jsonComputedOptions, React, _, StyledProps} from '@api'
import {AbstractSidebarStore, CatalogItemsListContextMenu, dataMapperConfig, FilterStore, ICatalogItemDetailsPathElement, SidebarAlign, SidebarStore, UNASSIGNED, VipLocalStore} from '@modules'
import {CatalogItemsDataVirtualRowStore} from '@modules/catalog/sidebar/CatalogItemsList/CatalogItemsDataVirtualRow'
import {ICatalogFieldConfig, isNumericAttribute, isTagField, isTagLibrary, LibraryItemConfig, LibraryItemConfigWrapper, nav, prettyPrint, StoresInstance, ui, WorkspaceConfig} from '@stores'
import {removeUndefinedsNested} from '@utils'
import {SearchField2, SearchField2Store} from 'centric-ui-components'
import {action, autorun, computed, observable, reaction, runInAction, toJS, trace} from 'mobx'
import {IDisposer} from 'mobx-utils'
import {useContext} from 'react'

export interface CatalogItemsListProps extends StyledProps {
  library?: LibraryItemConfigWrapper
  catalogField?: ICatalogFieldConfig
  workspace?: WorkspaceConfig
  className?: string
  titleStyle?: 'count' | 'name'
  isPopover?: boolean
  onSelect?: (selectedItem: IMappedCatalogItemData) => void
  onDelete?: (selectedItemId: string) => any
  selectedValues?: Set<string>
  align: SidebarAlign
  visible?: boolean
}

export interface IMappedCatalogItemData {
  images?: Array<{}>,
  title?: string,
  subtitle1?: string,
  hex?: 'hex',
  exId?: string,
  _id?: string,
  id?: string,
  name?: string,
  description?: string,
  attrName?: string,
  field?: string
  count?: number
}

export const CatalogItemsListStoreContext = React.createContext<CatalogItemsListStore>(undefined)
export const useCatalogItemsListStore = () => useContext(CatalogItemsListStoreContext)

export class CatalogItemsListStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Partial<CatalogItemsListProps> & { parentStores: StoresInstance }) {
    super(stores)
  }

  _toDispose: Array<IDisposer> = []

  dispose = () => {
    this._toDispose.forEach(f => f())
  }

  registerEffects = () => {
    const {workspaces, props} = this
    this.stores.catalog.componentStores.itemsList = this

    // console.log(`Mount CatalogItemsListStore`)
    this._toDispose.push(
      reaction(() => this.stores.catalog.refetchId, () => this.refetch()),
      autorun(() => {
        const {stores, library, assignableField, libraryWorkspace} = this
        runInAction(() => {
          this.overrideStores?.dispose()
          if (library) {
            // console.log({library, libraryWorkspace})
            this.overrideStores = new StoresInstance(
              `Catalog Items List for library - ${props.library?.id}`,
              libraryWorkspace ? workspaces.workspaceFor(libraryWorkspace._id) : stores.workspace,
              `${stores._id}-${props.library?.id}`,
              'library-items-list',
              {library: props.library, parent: stores}
            )
            this.overrideStores.sidebar = stores.sidebar
            this.overrideStores.catalog.componentStores.itemsList = this
          } else if (assignableField) {
            this.overrideStores = new StoresInstance(`Catalog Items List for library - ${assignableField.field}`, stores.workspace, `${stores._id}-${assignableField.field}`, 'library-items-list', {assignableField, parent: stores})
            this.overrideStores.sidebar = stores.sidebar
            this.overrideStores.catalog.componentStores.itemsList = this
          } else {
            this.overrideStores?.dispose()
          }
        })
      }),
      autorun(() => {
        const {visible, library, catalogField} = props
        if (visible && ((library && library != this.lastLibrary) || (catalogField && catalogField != this.lastCatalogField))) {
          runInAction(() => {
            if (this.lastLibrary || this.lastCatalogField) {
              this.reset()
              this.tempSearchText = null
              this.sidebar.searchText = ''
              this.searchField?.cancel?.()
            }

            this.lastLibrary = library
            this.lastCatalogField = catalogField
          })
        }
      }),

      autorun(() => {
        const {visible} = props
        if (visible) {
          runInAction(() => {
            if (this.sidebar.searchText) {
              this.tempSearchText = this.sidebar.searchText
            } else if (this.tempSearchText) {
              this.sidebar.searchText = this.tempSearchText
            }
          })
        }
      }, {name: `Copy search text into sidebar and run item list query when becoming visible`}),
      reaction(() => this.libraryQueryClause, clause => {
        if (clause) {
          this.lastLibraryQueryClause = clause
        }
      }))

    return this.dispose
  }

  @observable.ref overrideStores: StoresInstance
  lastLibrary
  lastCatalogField

  @observable copyDbString = false
  copyDocQueryToClipboard = () => {
    const {catalog} = this
    navigator.clipboard.writeText(`GET ${catalog.index}/_search\n${prettyPrint(this.queryForAssignableFieldES)}\n`)
    this.copyDbString = true
    setTimeout(() => this.copyDbString = false, 2000)
  }

  onContextMenu = e => {
    this.contextMenu.onComponentContextMenu(e, this)
  }

  get contextMenuItems() {
    return <CatalogItemsListStoreContext.Provider value={this}>
      <CatalogItemsListContextMenu/>
    </CatalogItemsListStoreContext.Provider>
  }

  /**
   * When we back button out of the library we don't want to 'forget' who we were as we're animating off screen
   */

  @computed get key() {
    return this.library?.name ?? this.assignableField?.field
  }

  @computed get catalogItemsListData() {
    const {virtualRowCount, virtualRows} = this
    return _.range(0, virtualRowCount).reduce((acc, i) => [...acc, ...(virtualRows.get(i)?.data ?? [])], [])
  }

  @observable.ref catalogItems = []
  @observable.ref seasonalData = []
  @observable prevPath: string

  @computed get hideUnassignedChip() {
    return this.loading && !this.virtualRows.get(0)?.data?.length
  }

  @computed get library(): LibraryItemConfigWrapper {
    const {props: {library, catalogField}} = this
    return catalogField && !library ? null : library
  }

  @computed get sidebar(): AbstractSidebarStore {
    return this.stores.sidebar[this.props.align]
  }

  @computed get catalog() { return this.overrideStores?.catalog ?? this.stores.catalog}

  @computed.struct get filter() {
    if (!this.library) return null // return ui.settings?.deleteInfo?.markAsDelete ? {query: this.es.filterForNonDeletedItems} : null
    const {library: {index}, catalog} = this
    return index ? {} : catalog.es.filterTermsFor({search: true}).filter
  }

  @computed get libraryWorkspace() {
    if (!this.library) return null
    const {stores: {config: {deployment}}, library: {index}} = this
    const workspace = deployment?.workspaces?.find(b => b?.library?.index === index || b?.catalog?.index === index)
    return workspace
  }

  // region Search

  @observable.ref searchField: SearchField2Store | null = null
  setSearchRef = r => this.searchField = r
  @observable searching = false

  @observable tempSearchText: string = null

  @computed get searchText() {
    return this.tempSearchText ? this.tempSearchText : this.sidebar.searchText
  }

  @computed get searchWords() {
    return (this.tempSearchText ? this.tempSearchText.trim().split(' ') : this.sidebar.searchWords).filter(s => s.length > 0)
  }

  @action onSearch = (e, v) => {
    const {sidebar, searching} = this
    if (!searching && v != sidebar.searchText) {
      this.searching = true
    }

    this.tempSearchText = v
    this._onSearch_debounced(e, v)
  }

  @action _onSearch_debounced = _.debounce(async (e, v) => {
    const {sidebar} = this
    if (sidebar.searchText != v) {
      runInAction(() => {
        this.virtualRowCount = 1
        //await this.loadCount()
        sidebar.searchText = v
        this.count = null
      })
    } else {
      this.searching = false
    }
  }, 200)

  @action getTagAssignedCount = async (tagPath: string, data, aggregateBy: string = 'id') => {
    const {catalog, es} = this
    const {hits: {hits}, aggregations: {count: {value}}} = await es.search(removeUndefinedsNested({
      meta: `CatalogItemsList.getTagAssignedCount${this.library?.label}`,
      index: catalog.index,
      body: {
        size: 10000,
        _source: ['_id'],
        aggs: {
          count: {value_count: {field: '_id'}}
        },
        query: {
          bool: {
            filter: [
              {
                bool: {
                  must: [
                    {term: {[`${tagPath}.${aggregateBy}`]: data?._id}}
                  ]
                }
              },
              // es.filterForNonDeletedItems
            ].filter(_.identity)
          }
        }
      }
    }), {log: {query: false, result: false}})
    return {assignedItemIds: hits?.map((hit) => { return {id: hit._id} }) ?? [], count: value}
  }

  @computed get isTag() {
    return isTagField(this.assignableField) //|| isTagField(this.catalogField)
  }

  @action onDelete = (data) => async () => {
    const {strings, library, isTag, getTagAssignedCount, flexLayout: {activeCatalogRoot: {catalog, catalog: {dialogs: {showConfirmationProductPopup}}}}} = this,
      offlineItems = catalog.offlineItemsAssignedToLibrary(library, data),
      field = this.assignableField ?? this.catalogField

    if (!field) throw new Error(`Unable to determine what we are deleting`)

    this.activeEditItem = {...data, action: 'delete'}

    if (isTag) {
      const {es, catalog: {catalogItemName, index}} = this,
        {tagType} = this,
        {assignedItemIds, count} = await getTagAssignedCount(field.field, data)

      showConfirmationProductPopup({
        message: strings.deleteTagMsg(data?.name, count + offlineItems.length, catalogItemName),
        show: true,
        operation: 'DELETE_TAG',
        id: data?._id,
        additionalData: {type: tagType ?? field?.def?.tagType, index: es.indexes.tag, name: data?.name, targetIds: assignedItemIds.map(item => item.id), targetIndex: index, tagPath: field?.field},
        onSuccess: this.refetch
      })
    } else if (_.includes(['attributes.MerchOptionVersionTags', 'tags'], library?.name)) {
      const {catalog: {catalogItemName, index}} = this,
        {tagType} = this,
        tagPath = library.assignTo ?? `attributes.${tagType}s`,
        {assignedItemIds, count} = await getTagAssignedCount(tagPath, data)

      showConfirmationProductPopup({
        message: strings.deleteTagMsg(data?.name, count + offlineItems.length, catalogItemName),
        show: true,
        operation: 'DELETE_TAG',
        id: data?._id,
        additionalData: {type: data?.type ?? tagType ?? field?.def?.tagType, index: library?.index, name: data?.name, targetIds: assignedItemIds.map(item => item.id), targetIndex: index, tagPath},
        onSuccess: this.refetch
      })
    } else {
      console.error(`Items does not support deletion NYI - `, this.assignableField)
    }
  }

  @action getActiveItem = (itemData) => {
    this.isUpdating = this.assignable = true
    this.isCreating = this.isCreating && false
    this.activeEditItem = itemData
  }
  // endregion

  @observable error: Error

  @computed get assignableField() : ICatalogFieldConfig {
    const {props} = this
    return props.library ? null : props.catalogField
  }

  @computed get tagType() {
    const {props: {library, catalogField, workspace}, catalog} = this

    return _.includes(['Tag', 'TagList'], catalogField?.def?.type) ? (catalogField?.def?.tagType ?? (catalogField ? `${workspace._id}.${catalogField.field}` : catalog.catalogConfig.tagType)) : null
  }

  @computed get compositeCustomSort() {
    if (!this.grouping) return undefined

    const {grouping: {firstOrderBy, firstCustomSort: customSort}} = this
    if (customSort) {
      return {
        custom_sort: {
          bucket_sort: {
            sort: [
              {
                max_score: {
                  order: customSort.orderDesc ? 'asc' : (firstOrderBy?.order ?? 'asc')
                }
              }
            ]
          }
        },
        max_score: {
          max: {
            'script': '_score'
          }
        }
      }
    }
    return undefined
  }

  @computed get grouping() {
    const {assignableField, catalog} = this
    return assignableField ? catalog.groupBy.groupingByName.get(assignableField.field) : null
  }

  @observable timesClicked = 0
  @observable groupCount = 0

  @computed(jsonComputedOptions) get groupsClause() {
    //trace()
    const {grouping, itemsPerPage, timesClicked, groupCount, compositeClause} = this
    if (!grouping || compositeClause) return undefined
    const r = grouping?.groupsClauseFor({itemsPerClick: itemsPerPage, timesClicked, groupCount})
    return r
  }

  @computed get compositeClause() {
    const {itemsPerPage, grouping} = this
    return grouping?.compositeGroupsClauseFor({itemsPerClick: itemsPerPage})
  }

  /**
   * For tags we need to query vip-tag for the overall list of tags and separately query the catalog index for grouped counts
   */
  @computed get countQueryTerms() {
    const {es, grouping, esField, tagType, assignableField, termsAgg, assignableField: {section, sort, def, esIndex}, filter, catalog} = this,
      sectionConfig = catalog.sectionConfigFor(section),
      isCustom = sort?.custom

    let query = {
      index: catalog.index,
      body: {
        size: 0,
        ...filter,
        aggs: {
          composite: {
            aggs: esIndex ? {
              item: {top_hits: {size: 1, _source: catalog.esFieldFor(assignableField)}}
            } : undefined,
            ...(sectionConfig?.viewOptions?.field ?
              {
                nested: {path: sectionConfig?.path},
                aggs: {
                  composite: termsAgg
                }
              } : termsAgg)
          }
        }
      }
    }

    if (isCustom && grouping) {
      const {grouping, compositeCustomSort, compositeClause, groupsClause} = this
      query.body = removeUndefinedsNested({
        size: 0,
        aggs: {
          composite: removeUndefinedsNested({composite: {...compositeClause.composite}, aggs: compositeCustomSort}),
          groups: groupsClause,
          count: {value_count: {field: esField}},
          itemCount: {value_count: {field: '_id'}},
          unassigned: {
            missing: {field: esField}
          }
        },
        query: grouping.functionScoreTerm
      })
    }

    if (!_.isEmpty(this.queryStringEscaped)) {
      const searchFields = [sectionConfig?.viewOptions?.field && section && `${section}.${sectionConfig?.viewOptions?.field}`].filter(v => v != 'id' && v != 'images' && v != 'hex' && v).map(f => `${f}.text`),
        should = [
          ...query.body.nested?.query?.bool?.should || [],
          ...(!_.isEmpty(searchFields) ? [
            {
              query_string: {
                fuzziness: 'AUTO',
                query: `*${es.tokenizeQueryString(this.queryStringEscaped)}*`,
                fields: searchFields,
                default_operator: 'AND'
              }
            }
          ] : [])
        ].filter(_.identity)

      query.body = removeUndefinedsNested({
        ...query.body,
        query: {
          ...(sectionConfig?.viewOptions?.field
              ? {
              nested: {
                ...query.body.query?.nested,
                path: section,
                query: {
                  ...query.body.query?.nested?.query,
                  bool: {
                    ...query.body.query?.nested?.query?.bool,
                    should,
                    minimum_should_match: should.length
                  }
                }
              }
            } : {
              bool: {
                ...query.body.query?.bool,
                should,
                minimum_should_match: should.length
              }
            })
        }
      })
    }

    return query
  }

  @computed get tagValuesQuery() {
    const {tagType, es, termsAgg} = this
    return !tagType ? null : {
      index: es.indexes.tag,
      body: {
        _source: ['name', 'id'],
        sort: [{name: {order: 'asc'}}],
        size: 10000,
        query: {
          bool: {
            must: [
              {term: {type: tagType}},
              {term: {_index: es.indexes.tag}},
              // es.filterForNonDeletedItems
            ].filter(_.identity)
          }
        },
        // Invalid here - we are querying the tag table, not the catalog
        // aggs: {
        //   composite: {
        //     ...termsAgg
        //   }
        // }
      }
    }
  }

  @computed get termsAgg() {
    const {esField, isTag, assignableField} = this
    return removeUndefinedsNested({
      terms: {
        field: esField,
        order: {'_key': 'asc'},
        missing: isNumericAttribute(assignableField?.def) ? undefined : assignableField?.def?.type === 'Boolean' ? undefined : UNASSIGNED,
        size: 1000
      }
    })
  }

  @computed get esField() {
    const {catalog, assignableField} = this

    return catalog.esFieldFor(assignableField, true)
  }
  @computed get queryForAssignableFieldES() {
    const {countQueryTerms, tagType} = this
    let query = removeUndefinedsNested(
      tagType
      ? this.tagValuesQuery
      : countQueryTerms)

    return query
  }

  @observable runningQuery = false

  @computed get lastPath() {
    return _.last(this.sidebar.path) as ICatalogItemDetailsPathElement
  }

  runQueryPromises: { [index: number]: Promise<any> } = {}

  @action runQuery = async (pageIndex: number = 0) => {
    if (!this.props.visible) return

    //console.trace(`runQuery(${pageIndex})`)
    const {_runQuery, library, libraryQueryClause, runQueryPromises, assignableField, catalog, stores: {sidebar}} = this
    if (!library && !assignableField) return null
    if (library && !libraryQueryClause) return null

    if (runQueryPromises[pageIndex] && library?.index && !isTagLibrary(library)) return this.runQueryPromises[pageIndex]

    // console.log(`Actually running query ${pageIndex}`)
    this.runQueryPromises[pageIndex] = new Promise(async (resolve, reject) => {
      try {
        const data = await _runQuery(pageIndex)
        library && catalog.registerLibraryItems({library, items: data})

        this.virtualRowCount = Math.min(Math.ceil(this.count / this.itemsPerPage), this.virtualRowCount)

        resolve(data)
      } catch (err) {
        reject(err)
      } finally {
        delete this.runQueryPromises[pageIndex]
      }
    })
    return this.runQueryPromises[pageIndex]
  }

  @computed get sectionValues() {
    const {assignableField, catalog} = this

    if (assignableField) {
      const {section} = assignableField
      const sectionConfig = catalog.sectionConfigFor(section)
      return catalog.sectionValuesFor(section)
    }

    return null
  }

  _runIndexItemListQuery = async (itemInput: { pageIndex: number, index: string, sortBy?: any, queryFields: Array<string>, queryClause: any, itemName: string }) => {
    const {es, itemsPerPage} = this,
      {pageIndex, index, sortBy, queryFields, queryClause, itemName} = itemInput
    const {hits: {hits, total}, aggregations: {count}} = await es.search(
      removeUndefinedsNested(
        {
          meta: `CatalogItemsList._runIndexItemListQuery(index: ${pageIndex}, name: ${itemName})`,
          index: index,
          body: {
            size: itemsPerPage,
            from: Math.max(0, pageIndex) * itemsPerPage,
            sort: sortBy ? {[sortBy]: {'order': 'asc', 'unmapped_type': 'keyword'}} : undefined,
            _source: queryFields ?? undefined,
            query: _.isEmpty(queryClause) ? undefined : queryClause,
            aggs: {
              count: {
                cardinality: {field: '_id'}
              }
            },
            track_total_hits: true
          }
        }), {log: {query: false, result: false}})

    const r = hits.map(({_id, _source}) => ({_id, ..._source}))
    this.count = total.value //count.value
    return r
  }

  _runQuery = async (pageIndex: number) => {
    const {library, assignableField, stores} = this
    if ((!library && !assignableField) || (library && !stores.sidebar?.left?.hasLibraryView)) {
      // this.searching = false
      return null
    }

    //console.log(`_runQuery(${pageIndex}, ${library?.name})`)
    this.runningQuery = true
    this.error = null
    try {
      const {queryFields, libraryQueryClause, itemsPerPage, dataMap} = this

      // region Assignable Field Query
      if (assignableField && !assignableField.esIndex) {
        return await this._runAssignableFieldQuery(pageIndex)
      } else {
        // endregion

        // region Library Items Query
        const {library, assignableField} = this,
          index = library?.index ?? assignableField?.esIndex

        if (index || library?.where) {
          //console.log({index, path, queryFields, queryClause})
          // We can directly query the source data instead of attached fields on our main board index
          const sortBy = library?.sortField ?? dataMap?.title
          return await this._runIndexItemListQuery({pageIndex, index, sortBy, queryFields, queryClause: libraryQueryClause, itemName: library?.name ?? assignableField?.label})
        }
          //endregion

          // this code cannot currently be hit
          // We are querying a subfield that is attached to our main board index.  ie, we have to aggregate
          // else if (assignableField?.field) {
          //   const {aggregations: {items: {buckets}}} = await es.search(
          //     removeUndefinedsNested({
          //       meta: `CatalogItemsList.runQuery(index: ${pageIndex}, aggregationInEs: ${assignableField.field}`,
          //       index: index || catalog.index, body: {
          //         size: 0,
          //         ...(filter),
          //         aggs: {
          //           items: {
          //             terms: {
          //               field: `${assignableField.field}${keyword ? '.keyword' : ''}` || '_id',
          //               order: {'_key': 'asc'},
          //               size: itemsPerPage,
          //               include: {
          //                 partition: Math.min(pageIndex, num_partitions - 1),
          //                 num_partitions
          //               }
          //             },
          //             aggs: {
          //               v: {top_hits: {size: 1, _source: queryFields.length ? {includes: queryFields} : undefined}}
          //             }
          //           }
          //         }
          //       }
          //     }), {log: {query: false, result: false}})
          //
          //   //console.log(buckets)
          //   const r = buckets.map(({key, doc_count, v: {hits: {hits: [{_id, _source}]}}}) => ({key, _id, count: doc_count, ..._source, ...(path ? _source[path] : {})}))
          //   //console.log(r)
          //   return r
        // }
        else {
          throw new Error(`Unhandled case`)
        }
      }
    } catch (err) {
      this.error = err
      throw err
    } finally {
      this.runningQuery = false
      this.searching = false
      delete this.runQueryPromises[pageIndex]

      if (this.count == 0) {
        this.loadingMore = false
      }
    }
  }

  /** ???? **/
  @action seasonalLibraryData = async (data) => {
    try {
      const {
          es, library, queryStringEscaped, catalog,
          catalog: {seasonSelectorFields, _fieldByName, filter: {state: {groups}}}
        } = this,
        hierarchyData = []
      let filterType: string = 'should', additionalWhere = {}
      seasonSelectorFields?.forEach(({lookup, field}) => {
        if (groups[field]?.selected.size || groups[lookup]?.selected.size) {
          // hierarchyData.push({terms: {[`${lookup}.name`+(_fieldByName[lookup]?.keyword?".keyword":"")]: Array.from(groups[field]?.selected.keys())}},
          hierarchyData.push({terms: {[`${field}.name` + (_fieldByName[field]?.keyword ? '.keyword' : '')]: Array.from(groups[field]?.selected.keys()).filter(_.identity)}})
        }
      })
      if (!_.size(hierarchyData)) {
        seasonSelectorFields?.forEach(({field, lookup}) => {
          hierarchyData.push({exists: {field}}, {exists: {field: lookup}})
        })
        filterType = 'should'
      }
      additionalWhere['minimum_should_match'] = 1
      if (_.size(hierarchyData)) {
        // const tagLibraryTerm = library?.isTagLibrary && catalog.getTagLibraryClause(),
        const source = [`${library.assignTo}.id`, `${library.assignTo}.name`],
          tagName = `${library.assignTo}.name`,
          {aggregations: {products_with_tags: {buckets}}} = await es.search(removeUndefinedsNested({
            meta: `CatalogItemsList.Seasonal${library?.label}`,
            index: catalog?.index,
            body: {
              size: 0,
              query: {
                bool: {
                  [filterType]: hierarchyData
                }
              },
              aggs: {
                products_with_tags: {
                  aggs: {
                    tags: {
                      top_hits: {
                        _source: source ?? undefined
                      }
                    }
                  },
                  terms: {
                    field: tagName,
                    min_doc_count: 1,
                    order: {_key: 'asc'},
                    size: 100000
                  }
                }
              }
            }
          }), {log: {query: false, result: false}})
        const searchRegExp = new RegExp(queryStringEscaped, 'gi'),
          result = _.uniqBy(_.flatMap(buckets, b => _.flatMap(b.tags?.hits?.hits || [], h => _.get(h?._source, library.assignTo) || [])), 'id').filter(thisTag => queryStringEscaped ? thisTag?.name?.match(searchRegExp) : thisTag)
        if (_.size(this.activeEditItem)) {
          const {activeEditItem, activeEditItem: {action, name}} = this
          switch (action) {
            case 'delete':
              this.catalogItems = this.catalogItems.filter((item) => item._id !== activeEditItem._id)
              this.activeEditItem = {}
              break
            case 'update':
              this.catalogItems = this.catalogItems.map((item) => {
                const {id, _id} = item, itemId = id || _id
                return itemId == activeEditItem?._id ? {...item, name} : item
              })
              this.activeEditItem = {}
              break
            default:
              console.log('no action specified for catalog item')
              break
          }
        }
        this.seasonalData = result?.length && result || []
        this.catalogItems = _.uniqBy(_.union(this.catalogItems, data), '_id')
        //this.catalogItemsListData = _.size(this.catalogItems) > 0 ? _.differenceWith(searchText ? data : this.catalogItems, result?.length && result, _.isEqual) : data
      }
    } catch (err) {
      this.error = err
      throw err
    }
  }

  @computed get draggable(): boolean {
    const {catalog: {perspective}, dragDrop, library, assignableField} = this

    const draggable = !_.isEmpty(dragDrop.allowedDropInteractionsFor({dragType: library?.index, meta: dragDrop.ASSIGN_LIBRARY})) || !_.isEmpty(dragDrop.allowedDropInteractionsFor({dragType: assignableField?.field}))
    return draggable
  }

  @computed get dataMap() {
    if (this.assignableField?.def?.type == 'Enum') {
      return dataMapperConfig.enumDataMapperConfig
    } else if (this?.library) {
      return this.catalog.dataMapForLibrary(this.library)
    } else {
      return dataMapperConfig.specDataMapperConfig
    }
  }

  @computed get label() {
    const {library, assignableField, catalog} = this
    return library ? (library?.config?.label ?? catalog.labelForLibrary({id: library.id}, 'plural'))
                   : assignableField ? catalog.labelForField(assignableField) : null
  }

  @observable lastCount: number = null
  @observable count: number = null

  @action onClick = (_id) => () => {
    const {sidebar: {selectedPathItem}} = this
    selectedPathItem && this.library && (selectedPathItem.where = [{value: _id}])
  }

  // region Drag and Drop

  // @action onDragStart = ({ _id, label }) => (e: React.DragEvent<HTMLElement>) => {
  //   e.stopPropagation()

  //   const { library: { name }, catalogField: { field } } = this
  //   dragDrop.setDragItem(e, {
  //     id: _id,
  //     name,
  //     label,
  //     dragType: field,
  //     dragFrom: 'list'
  //   })
  // }

  // @action onDragEnd = (_id) => () => {
  // }

  // endregion

  // region Create Item

  @observable isCreating = false
  @observable isUpdating = false
  @observable isEditMode = false
  @observable assignable = false
  @observable.ref activeEditItem: any = {}

  // endregion

  itemsPerPage = 20

  @observable loadingMore = false
  @observable virtualRowCount = 1

  @action loadMore = () => {
    if (!this.loading && this.hasMore) {
      this.virtualRowCount++
      this.loadingMore = true
    }
  }

  @computed get loading() {
    return this.loadingMore || Array.from(this.virtualRows.values()).some(s => s.loading)
  }

  @computed get hasMore() {
    return this.catalog.catalogConfig
           && !this.loading
           && this.count !== 0
           && this.count > this.virtualRowCount * this.itemsPerPage
           && this.count > this.catalogItemsListData?.length
  }

  virtualRows = observable.map<number, CatalogItemsDataVirtualRowStore>({}, {deep: false})

  @action reset = () => {
    this.virtualRowCount = 1
    this.loadingMore = true
    this.count = null

    this.catalogItems = null
    //await Promise.all([this.loadCount()])
  }

  @action resetPaging = () => {
    this.virtualRowCount = 1
    //this.loadingMore = true
  }

  @computed get num_partitions() {
    return Math.max(Math.ceil((this.count || 0) / this.itemsPerPage), 1)
  }

  @computed get queryStringEscaped() {
    const {es, sidebar: {searchText}} = this

    return searchText ? es.escapeString(searchText).trim() : ''
  }

  @computed get catalogField() {
    if (this.library) {
      const {library: {catalogFields}, catalog: {_fieldByName}} = this
      return catalogFields?.length ? _fieldByName[_.first(catalogFields)] : null
    }

    return this.assignableField
  }

  @observable.ref lastLibraryQueryClause

  @computed get libraryQueryClause() {
    const {es, catalog, overrideStores} = this
    if (!this.library || catalog.loading || !overrideStores) return this.lastLibraryQueryClause
    const {searchQueryFields, queryStringEscaped, catalogField, library, library: {name, seasonal}, overrideStores: {filter}} = this,
      fetchTagsByLibrary = isTagLibrary(this.library) ? catalog.getTagLibraryClause() : undefined,
      wheres = [...catalog.whereForLibrary(library, false)]

    if (fetchTagsByLibrary) {
      wheres.push(fetchTagsByLibrary)
    }

    wheres.push(...(filter?.filterTermsFor({}, {fromFilter: false}) ?? []))

    let should = [
        ...wheres.flat(),
        !_.isEmpty(queryStringEscaped) && {
          query_string: {
            fuzziness: 'AUTO',
            query: `*${es.tokenizeQueryString(queryStringEscaped)}*`,
            fields: [...searchQueryFields.filter(v => v != 'id' && v != 'images' && v != 'hex').map(f => `${f}.text`)],
            default_operator: 'AND'
          }
        }
      ].filter(_.identity),
      result: any = {
        bool: {
          minimum_should_match: should.length,
          should
        }
      }

    if (catalogField?.def?.type == 'Enum') {
      let attrName = catalogField.field.substring(catalogField.field.lastIndexOf('.') + 1)
      if (attrName.endsWith(`_Enum`)) {
        attrName = attrName.substring(0, attrName.lastIndexOf('_'))
      }
      _.merge(result, {term: {[`attrName.keyword`]: {value: catalogField?.def?.enumType}}})
    }

    const {es: {catalogSeasonalTerms}} = catalog
    if (seasonal && !_.isEmpty(catalogSeasonalTerms)) {
      result.bool.must.push(catalogSeasonalTerms)
    }

    // if (ui.settings?.deleteInfo?.markAsDelete) {
    //   result = {
    //     ...(result || {}),
    //     bool: {
    //       ...result?.bool,
    //       filter: [
    //         ...(result?.bool?.filter || []),
    //         es.filterForNonDeletedItems
    //       ].filter(_.identity)
    //     }
    //   }
    // }

    return _.isEmpty(result) ? undefined : result
  }

  @computed.struct get libraryCountSearchTerms() {
    if (!this.library) return null

    const {library: {index}, libraryQueryClause} = this

    return removeUndefinedsNested({
      meta: `CatalogItemsList.loadCount()`,
      index,
      body: {
        size: 0,
        aggs: {
          count: {value_count: {field: '_id'}}
        },
        query: _.isEmpty(libraryQueryClause) ? undefined : libraryQueryClause
      }
    })
  }

  // @action loadCount = async () => {
  //   if (!this.library) return null
  //   const { aggregations: { count: { value } } } = await es.search(this.libraryCountSearchTerms)
  //   this.count = value
  // }

  @action onClickAdd = () => {
    this.isEditMode = false
    this.isCreating = !this.isCreating
  }

  @action toggleEditLibraryMode = () => {
    this.isCreating = false
    this.isEditMode = !this.isEditMode
  }

  @computed get renderer() {
    const {library, assignableField} = this
    return assignableField?.esIndex ? 'card' : assignableField ? 'chip' : (library?.renderer ?? 'card')
  }

  @computed get dataMapExtraQueryFields(): Array<string> {
    /* const extraPreviewCard = this.library?.cardUI?.main?.sections[0]?.extraPreviewCard || [],
       {workspaces: {byIndex}} = stores,
       libraryIndex = this.library?.index,
       extraQueryFields = _.chain(extraPreviewCard)
         .map(d => ({..._.find(byIndex[libraryIndex]?.catalog?.fields, e => e.field == d.field), ...d}))
         .value()
     return extraQueryFields*/

    const {library} = this
    return _.flatMap(
      [...(library?.cardUI?.main?.sections ?? []), ...(library?.cardUI?.secondary?.sections ?? [])].reduce(
        (acc, v) => {
          return [...acc, v.fields?.map(f => f.field)]
        }, []
      ))
  }

  @computed get imagesFields() {
    const {library} = this
    return _.compact([
      library?.cardUI?.main?.image?.field || 'images',
      library?.cardUI?.secondary?.image?.field,
      library?.cardUI?.secondary?.image?.caption?.field
    ])
  }

  @computed get librarySourceFields() {
    if (!this.libraryWorkspace) return []
    if (_.size(this.libraryWorkspace?.catalog?._source)) {
      return this.libraryWorkspace?.catalog?._source
    }
    return []
  }

  @computed get queryFields() {
    if (!this.library) return null
    const {dataMap, dataMapExtraQueryFields, imagesFields, librarySourceFields} = this

    const queryFields = [..._.values(dataMap), ...dataMapExtraQueryFields, ...imagesFields, ...librarySourceFields].filter(_.identity).filter(_.isString)

    return _.uniq(queryFields)
  }

  @computed get searchQueryFields() {
    return this.queryFields
  }

  @action toggleListItem = (data?: IMappedCatalogItemData, isSelected?: boolean) => () => {
    const {field} = this.catalogField || {}
    const selectedItem = _.isUndefined(field) ? data : {...data, field}
    !isSelected ? this.props.onSelect(selectedItem) : this.props.onDelete(selectedItem.id)()
  }

  @action navigateToBoard = () => {
    const {libraryWorkspace} = this
    nav.siteHeader_navigateToWorkspace(libraryWorkspace, undefined, true)
  }

  @action refetch = async () => {
    await Promise.all(Array.from(this.virtualRows.values()).map(vr => vr.runVirtualRowQuery()))
  }

  @observable countByValue: Record<string, number> = {}

  private _runAssignableFieldQuery = async (pageIndex: number) => {
    const {strings, es, assignableField, assignableField: {field, esIndex, def, section}, queryFields, catalog, c8} = this,
      sectionConfig = catalog.sectionConfigFor(section),
      sectionValues = catalog.sectionValuesFor(section),
      searchRegEx = new RegExp(this.queryStringEscaped, 'gi')

    //console.log(sectionConfig, sectionValues)
    if (!sectionConfig?.viewOptions?.field && sectionValues === null) return null

    let {hits, aggregations: aggs} = await es.search({
      meta: `CatalogItemsList.runQuery(index: ${pageIndex}, field: ${field})`,
      index: catalog.index,
      ...this.queryForAssignableFieldES
    }, {log: {query: false, result: false}})


    if (!aggs) {
      const {aggregations} = await es.search({
        meta: `CatalogItemsList.getFieldCounts(index: ${pageIndex}, field: ${field})`,
        index: catalog.index,
        ...this.countQueryTerms
      }, {log: {query: false, result: false}})
      aggs = aggregations
    }

    this.countByValue = (aggs?.composite?.buckets ?? []).reduce((acc, {doc_count, key}) => {
      const v = _.isPlainObject(key) ? _.get(key, field) : key
      return {...acc, [v?.toString() ?? UNASSIGNED]: doc_count}
    }, {})

    const {countByValue} = this

    // console.log(toJS(countByValue))

    if ((def?.type == 'Enum' || def?.type === 'EnumList') && (def?.enumType || def.enumValues)) {
      const enumValues = await c8.getEnumValues(assignableField)
      // console.log(toJS(enumValues))
      //return enumValues

      const filtered = _.flatMap(enumValues.map(v => {
        if (!sectionConfig?.viewOptions?.field) {
          return ({...v, _id: v.description || v.name})  // YFTS-7837 Descriptions that are '' break ?? here
        } else {
          return sectionValues.map(({key}) => ({...v, _id: v.name, sectionValue: key, name: `${key || strings.noValue} - ${v.name}`}))
        }
      })).filter(d => {
        const r = String(d.description || d.name).match(searchRegEx)
        // console.log(d, r)
        return r
      }).map(v => ({...v, count: countByValue[(v._id ?? UNASSIGNED).toString()] ?? 0}))

      // console.log({filtered, countByValue: toJS(this.countByValue), field, enumValues, hits, aggregations})

      this.count = filtered.length
      return filtered
      // DEEPAK-NACHAN : Please DO NOT remove this false block code
    } else if (false && (def?.type == 'Ref' || def?.type === 'RefList') && assignableField?.esIndex) {
      let should = [
          !_.isEmpty(this.queryStringEscaped) && {
            query_string: {
              fuzziness: 'AUTO',
              query: `*${es.tokenizeQueryString(this.queryStringEscaped)}*`,
              fields: [...['name'].map(f => `${f}.text`)],
              default_operator: 'AND'
            }
          }
        ].filter(_.identity),
        result: any = {
          bool: {
            minimum_should_match: should.length,
            should
          }
        }
      // if (ui.settings?.deleteInfo?.markAsDelete) {
      //   result = {
      //     ...(result || {}),
      //     bool: {
      //       ...result?.bool,
      //       filter: [
      //         ...(result?.bool?.filter || []),
      //         es.filterForNonDeletedItems
      //       ].filter(_.identity)
      //     }
      //   }
      // }
      return await this._runIndexItemListQuery({pageIndex, index: assignableField?.esIndex || catalog.index, queryFields, queryClause: result, itemName: field})
    } else {
      const {assignableField} = this,
        fieldType = assignableField?.def?.type

      // await es.msearch({
      //   body: msearchBody,
      // }, {log: {query: true, result: true}})

      const {items, total: {value, relation}, aggregations} = es.unwrapHitsResult<{ _id, name?: string }>({hits, aggregations: aggs})
      this.count = value

      let r = items.map(v => ({...v, key: v._id, key_as_string: v.name ?? v._id ?? UNASSIGNED, count: countByValue[v.name ?? v._id ?? UNASSIGNED] ?? 0}))

      const keysOnHand = new Set(r.map(v => v.key_as_string))

      // We need to look up tags (defined by the user) and existing data already set to values not yet defined in the tag table
      if (aggregations) {
        const buckets: Array<{ key, key_as_string, doc_count, item? }> = aggregations?.composite?.buckets ?? aggregations?.composite?.composite?.buckets

        // Add true/false if missing
        if (def?.type === 'Boolean' && buckets?.length < 2) {
          const hasTrue = buckets.some(f => f.key_as_string == 'true')
          const hasFalse = buckets.some(f => f.key_as_string == 'false')

          !hasFalse && buckets.push({key: 0, key_as_string: 'false', doc_count: 0})
          !hasTrue && buckets.push({key: 1, key_as_string: 'true', doc_count: 0})
        }

        const aggs = [...r, ..._.flatMap<any>(
          buckets?.map(({key: keyVal, doc_count, item, ...rest}) => {
            const key_as_string = def?.type === 'Boolean' ? rest.key_as_string : _.isString(keyVal) || _.isNumber(keyVal) ? keyVal.toString() : rest.key_as_string

            if (keysOnHand.has(keyVal) || keysOnHand.has(key_as_string)) return false

            if (def?.type === 'Boolean') {
              const key = (def.booleanLabels ? def.booleanLabels[`${key_as_string}Label`] : key_as_string === 'true' ? _.capitalize(strings.yes) : _.capitalize(strings.no))
                          ?? (key_as_string === 'true' ? _.capitalize(strings.yes) : _.capitalize(strings.no))
              const r = {
                key: `${key}_${doc_count}`,
                _id: keyVal === 1 || keyVal === true,
                name: key,
                count: doc_count
              }

              if (!sectionConfig?.viewOptions?.field) {
                return r
              } else {
                return sectionValues.filter(v => v.key).map(({key}) => ({...r, count: null, sectionValue: key, name: `${key || strings.noValue} - ${r.name}`}))
              }
            }

            // Don't include unassigned values
            if (keyVal === UNASSIGNED || keyVal[field] === null) return null

            const key = (key_as_string === '' || keyVal[field] === ''
                          ? strings.noValue
                          : _.isObject(keyVal)
                            ? keyVal[field]
                            : keyVal) ?? keyVal

            return {key, _id: key, count: doc_count, name: key, item: item ? _.get(_.first(es.unwrapHitsResult(item).items), field) : null}
          }))].filter(_.identity).filter(d => String(d.name).match(searchRegEx))

        this.count = aggs.length
        r = sectionValues
               ? _.sortBy(aggs, a => a.name)
               : (fieldType === 'Ref' || fieldType === 'RefList')
                 ? aggs?.map(f => ({...f, _id: _.isArray(f?.item) ? catalog.idFor(f?.item?.find(i => i.name == f._id))
                                                                  : catalog.idFor(f?.item) ?? f._id}))
                 : aggs
      }

      return r
    }
  }

  @computed get showUnassignChip() {
    const {renderer, props: {isPopover}, draggable, hideUnassignedChip, assignableField, count, catalogItemsListData} = this
    return !hideUnassignedChip && assignableField?.def?.type != 'Boolean' && !_.isBoolean(assignableField?.showAsLibrary) && assignableField?.showAsLibrary?.unassign !== false && _.size(catalogItemsListData) > 0 && count != null && !isPopover && draggable && renderer == 'chip'
  }
}

// endregion



// WEBPACK FOOTER //
// ./src/modules/catalog/sidebar/CatalogItemsList/CatalogItemsListStore.tsx